import React, { useState } from 'react';
/**
 * LiveRulebookCard — Renders a single ModuleRulebook from the live
 * RULEBOOKS_REGISTRY (governance/rulebooks). Phase 3 Strak-Trek.
 *
 * Replaces the static markdown-metadata RulebookCard for module rulebooks.
 * Every section here is sourced from executable code — what you see is what
 * the pipeline actually enforces.
 *
 * Also surfaces a "Toekomstig (roadmap)" panel sourced from
 * src/governance/rulebooks/roadmaps.ts — aspirational future question types
 * and visuals that aren't enforced yet but signal the design trajectory.
 */
import { ChevronDownIcon, ChevronRightIcon, BookOpenIcon, CheckCircleIcon, TypeIcon, AlertTriangleIcon, PaletteIcon, LayersIcon, SparklesIcon } from 'lucide-react';
import type { ModuleRulebook } from '../../../../governance';
import { getModuleRoadmap } from '../../../../governance';
import { RULEBOOK_COLOR_MAP } from './governanceRulesData';
type Props = {
  rulebook: ModuleRulebook;
  color: string;
};
export function LiveRulebookCard({
  rulebook,
  color
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const c = RULEBOOK_COLOR_MAP[color] ?? RULEBOOK_COLOR_MAP.blue;
  const notationFlags: string[] = [];
  if (rulebook.notation.requireLatexFractions) notationFlags.push('LaTeX-breuken');
  if (rulebook.notation.requireCommaDecimals) notationFlags.push('Komma-decimalen');
  if (rulebook.notation.requireSuperscriptUnits) notationFlags.push('Superscript-eenheden (m², m³)');
  if (rulebook.notation.requireEuroComma) notationFlags.push('Euro-komma');
  const roadmap = getModuleRoadmap(rulebook.moduleId);
  return <div className={`rounded-lg border ${c.border} bg-white overflow-hidden`} data-id="element-4661" data-mp="1tjxtm4g3qa1w">
      <button className="flex w-full items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-4662" data-mp="1xjzqm0aw6fau">
        <BookOpenIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${c.badge.split(' ')[1]}`} data-id="element-4663" data-mp="1ok2ytpt7h0bk" />
        <div className="min-w-0 flex-1" data-id="element-4664" data-mp="3qa0qrugyu63o">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-4665" data-mp="3qb3ig4m44ckn">
            <span className="font-semibold text-gray-800" data-id="element-4666" data-mp="3lhh3g5sh7wlo">
              {rulebook.displayName}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.badge}`} data-id="element-4667" data-mp="3hgx6b27wkszj">
              {rulebook.shortCode}
            </span>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-4668" data-mp="d1fk6rvqkw75">
              live
            </span>
            {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100 flex items-center gap-1" data-id="element-4669" data-mp="3v7ae94kso7ov">
                  <SparklesIcon className="h-3 w-3" data-id="element-4670" data-mp="2jsdmvo1516ec" />
                  {roadmap.futureQuestionTypes.length + roadmap.futureVisuals.length}{' '}
                  toekomstig
                </span>}
          </div>
          <p className="text-sm text-gray-600 mt-1" data-id="element-4671" data-mp="2eokvb8ki5s12">
            {rulebook.didactics.coreSummary}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-4672" data-mp="2jlus59a815yx">
            {rulebook.didactics.principles.slice(0, 4).map(p => <span key={p} className="rounded px-1.5 py-0.5 text-[10px] font-mono bg-gray-50 text-gray-500 border border-gray-100" data-id="element-4673" data-mp="2sfhn4x6lu572">
                {p}
              </span>)}
          </div>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-4674" data-mp="32ef8xscnbkmd">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-4675" data-mp="37bfg66l8etf2" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-4676" data-mp="1805xlgovxubm" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${c.border} ${c.bg} px-4 py-3 space-y-4`} data-id="element-4677" data-mp="1cxxqacxctkgv">
          {/* Key rules */}
          <Section icon={<CheckCircleIcon className="h-4 w-4 text-gray-400" data-id="element-4679" data-mp="33e9rpxhfwtn2" />} title="Kernregels" data-id="element-4678" data-mp="33eruze2is84h">
            <ul className="space-y-1.5" data-id="element-4680" data-mp="2ygggur56zbgg">
              {rulebook.keyRules.map((rule, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-4681" data-mp="3cfpy7u54wbm0" data-mpi={i}>
                  <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" data-id="element-4682" data-mp="2jrkd36a64kln" />
                  {rule}
                </li>)}
            </ul>
          </Section>

          {/* Didactics */}
          <Section icon={<LayersIcon className="h-4 w-4 text-gray-400" data-id="element-4684" data-mp="2tj810x7injid" />} title="Didactische context" data-id="element-4683" data-mp="2thwvosyfa3p5">
            <div className="space-y-1.5 text-sm" data-id="element-4685" data-mp="2oply3wr5mtzr">
              <DetailRow label="Schoolboek-methodes" value={rulebook.didactics.schoolbookReferences.join(', ')} data-id="element-4686" data-mp="210uehtrmrjyl" />
              <DetailRow label="Misconceptie-domein" value={<code className="font-mono text-xs" data-id="element-4688" data-mp="bi45wb8kwvyk">
                    {rulebook.didactics.misconceptionDomain}
                  </code>} data-id="element-4687" data-mp="1w06jkx0a6iz3" />
            </div>
          </Section>

          {/* Notation */}
          {(notationFlags.length > 0 || (rulebook.notation.forbiddenPatterns?.length ?? 0) > 0) && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4690" data-mp="v8pnfji7mel5" />} title="Notatie" data-id="element-4689" data-mp="v7y0ex2ohp6h">
              {notationFlags.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-4691" data-mp="lajn36c1xgie">
                  {notationFlags.map(flag => <span key={flag} className="rounded-full px-2 py-0.5 text-xs bg-white border border-gray-200 text-gray-700" data-id="element-4692" data-mp="q8bp4t6euj0b">
                      {flag}
                    </span>)}
                </div>}
              {rulebook.notation.forbiddenPatterns?.map((p, i) => <p key={i} className="text-xs text-red-600" data-id="element-4693" data-mp="2mvw4t9f7tom9">
                  <code className="font-mono bg-red-50 px-1.5 py-0.5 rounded mr-2" data-id="element-4694" data-mp="2mwfl6lp7w4y6">
                    {p.pattern}
                  </code>
                  {p.label}
                </p>)}
            </Section>}

          {/* Visuals */}
          {(rulebook.visuals.preferredAidTypes?.length ?? 0) > 0 && <Section icon={<PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-4696" data-mp="236lopubk51nm" />} title="Voorkeurs-visuals" data-id="element-4695" data-mp="237wrprx6i5do">
              <div className="flex flex-wrap gap-1.5" data-id="element-4697" data-mp="1z6uu1fiwu1dt">
                {rulebook.visuals.preferredAidTypes?.map(v => <span key={v} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4698" data-mp="1u91ngldp35d0">
                    {v}
                  </span>)}
              </div>
              {rulebook.visuals.familyVisualHints && rulebook.visuals.familyVisualHints.length > 0 && <div className="mt-2 space-y-1" data-id="element-4699" data-mp="4w4o8cb6aykw">
                    {rulebook.visuals.familyVisualHints.map((h, i) => <p key={i} className="text-xs text-gray-600" data-id="element-4700" data-mp="enrar4syok2y" data-mpi={i}>
                        <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded mr-1.5" data-id="element-4701" data-mp="eq3of5ros8a7">
                          {h.familyIdPattern}
                        </code>
                        {h.note}
                      </p>)}
                  </div>}
            </Section>}

          {/* Question types */}
          {rulebook.questionTypes.preferredTypes.length > 0 && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4703" data-mp="12cjxqbrlvhlf" />} title="Voorkeurs-vraagtypes" data-id="element-4702" data-mp="12dvcf6mla9rb">
              <div className="flex flex-wrap gap-1.5" data-id="element-4704" data-mp="16dlywy5323q1">
                {rulebook.questionTypes.preferredTypes.map(t => <span key={t} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4705" data-mp="1seml6esvlcsq">
                    {t}
                  </span>)}
              </div>
              {rulebook.questionTypes.customTypeRoles && rulebook.questionTypes.customTypeRoles.length > 0 && <div className="mt-2 space-y-1" data-id="element-4706" data-mp="2b91k64f6t5gk">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold" data-id="element-4707" data-mp="2673vuvrxjkgr">
                      Custom-rollen
                    </p>
                    {rulebook.questionTypes.customTypeRoles.map(r => <div key={r.type} className="text-xs text-gray-600 flex items-center gap-2" data-id="element-4708" data-mp="2g48g2q27t5ux">
                        <code className="font-mono" data-id="element-4709" data-mp="ltlkdfn5kw3c">{r.type}</code>
                        <span className="text-gray-400" data-id="element-4710" data-mp="2xf40aej5d1m7">·</span>
                        <span data-id="element-4711" data-mp="2xhzwnyfcji8j">{r.didacticRole}</span>
                        <span className="text-gray-400" data-id="element-4712" data-mp="32dp1ag683pbp">·</span>
                        <span data-id="element-4713" data-mp="2npu9r33ybr07">DOK {r.preferredDok}</span>
                      </div>)}
                  </div>}
            </Section>}

          {/* Distractor traps */}
          {rulebook.distractorTraps.length > 0 && <Section icon={<AlertTriangleIcon className="h-4 w-4 text-gray-400" data-id="element-4715" data-mp="2iqrda721et3u" />} title="Trap-distractors (val-afleiders)" data-id="element-4714" data-mp="2enuotzptg2rz">
              <ul className="space-y-1.5" data-id="element-4716" data-mp="24rzzymv01bgr">
                {rulebook.distractorTraps.map(trap => <li key={trap.id} className="text-sm" data-id="element-4717" data-mp="29quhllh8ctza">
                    <p className="font-medium text-gray-800" data-id="element-4718" data-mp="2zlj7r46yd1x">{trap.label}</p>
                    <p className="text-xs text-gray-600" data-id="element-4719" data-mp="2xijugh5f7kj">{trap.description}</p>
                  </li>)}
              </ul>
            </Section>}

          {/* Toekomstig (roadmap) — aspirational, not enforced */}
          {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/40 p-3" data-id="element-4720" data-mp="1asvmpd0q660e">
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2 flex items-center gap-1.5" data-id="element-4721" data-mp="1fqx3fraoht8p">
                  <SparklesIcon className="h-4 w-4" data-id="element-4722" data-mp="23e59ibfxl6bs" />
                  Toekomstig — roadmap (niet afgedwongen)
                </p>
                <p className="text-[11px] text-violet-700/80 mb-3 leading-relaxed" data-id="element-4723" data-mp="1tibo02k8i2hv">
                  Aspirationele vraagtypes en visuals die we voor deze module
                  willen bouwen. Geen pipeline-check, wel ontwerprichting.
                </p>

                {roadmap.futureQuestionTypes.length > 0 && <div className="mb-3" data-id="element-4724" data-mp="n2rhj17w1y7a">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4725" data-mp="mznalw31gocx">
                      <TypeIcon className="h-3 w-3" data-id="element-4726" data-mp="ajnif7rcffyr" />
                      Vraagtypes ({roadmap.futureQuestionTypes.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4727" data-mp="oi40fuunosw7">
                      {roadmap.futureQuestionTypes.map(t => <li key={t.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4728" data-mp="jhzie0e3nq3v">
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4729" data-mp="5k15kzffg1qh">
                            <span className="font-medium text-gray-800" data-id="element-4730" data-mp="5m44ya2gz77v">
                              {t.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4731" data-mp="398clirbnden3">
                              {t.id}
                            </code>
                            {t.tierWindow && <span className="text-[10px] text-gray-500" data-id="element-4732" data-mp="186dcue67xhv2">
                                tier {t.tierWindow.min}–{t.tierWindow.max}
                              </span>}
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4733" data-mp="1mvjc2252382y">
                            {t.rationale}
                          </p>
                          {t.targetFamilyPatterns && t.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4734" data-mp="u981bynqjqsf">
                                {t.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4735" data-mp="13824y8zhhhfh">
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}

                {roadmap.futureVisuals.length > 0 && <div data-id="element-4736" data-mp="3lduu54n99vhh">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4737" data-mp="3leecgmgtilw1">
                      <PaletteIcon className="h-3 w-3" data-id="element-4738" data-mp="3gftc8n1kg27l" />
                      Visuals ({roadmap.futureVisuals.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4739" data-mp="37dk0xuyh44t5">
                      {roadmap.futureVisuals.map(v => <li key={v.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4740" data-mp="32fa3tet6uu4w">
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4741" data-mp="10n45eqh9vt8n">
                            <span className="font-medium text-gray-800" data-id="element-4742" data-mp="3ti42yhdj3g5n">
                              {v.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4743" data-mp="2b1almlyf3tu">
                              {v.id}
                            </code>
                            <span className={`text-[9px] uppercase tracking-wide px-1 rounded ${v.usage === 'stimulus' ? 'bg-blue-50 text-blue-700' : v.usage === 'support' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-4744" data-mp="3pexx3aubvqzp">
                              {v.usage}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4745" data-mp="35t2w2ykcgd2h">
                            {v.rationale}
                          </p>
                          {v.targetFamilyPatterns && v.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4746" data-mp="2d507f0p4rgkz">
                                {v.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4747" data-mp="288jmsgh53kcn">
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}
              </div>}
        </div>}
    </div>;
}
function Section({
  icon,
  title,
  children
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-4748" data-mp="3atk2xd966tmj">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5" data-id="element-4749" data-mp="3attsx0u7t0s8">
        {icon}
        {title}
      </p>
      {children}
    </div>;
}
function DetailRow({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div className="flex items-start gap-2 text-sm" data-id="element-4750" data-mp="3ruy0cfb25hu9">
      <span className="text-gray-500 min-w-[140px]" data-id="element-4751" data-mp="3ru65j6pabo51">{label}:</span>
      <span className="text-gray-700" data-id="element-4752" data-mp="gsaadlwkuh3">{value}</span>
    </div>;
}